import { DashboardModal } from "@dashboard/components/Modal";
import { Mono } from "@dashboard/extensions/views/EditCustomExtension/components/TokenCreateDialog/Mono";
import { useClipboard } from "@dashboard/hooks/useClipboard";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { secretDialogMessages } from "../../messages";
import { getAuthorizationHeader } from "../../utils";

const panelStyles = {
  padding: 4,
  backgroundColor: "default2",
  borderRadius: 4,
  __wordBreak: "break-all",
} as const;

const CopyButton = ({ text, label, testId }: { text: string; label: string; testId: string }) => {
  const intl = useIntl();
  const [copied, copy] = useClipboard();

  return (
    <Button variant="secondary" marginTop={2} onClick={() => copy(text)} data-test-id={testId}>
      {copied ? intl.formatMessage(secretDialogMessages.copied) : label}
    </Button>
  );
};

export interface AtticApiKeySecretDialogProps {
  /** The secret, which the API shows this once; null keeps the dialog shut. */
  secret: string | null;
  /** A new key's first secret, or a second one added to rotate. */
  kind: "created" | "rotated";
  onClose: () => void;
}

/**
 * The only time a secret is readable. It lives in the opening view's state
 * alone, never in the URL, history state or the Apollo cache, so closing this
 * dialog is the last anyone sees of it.
 */
export const AtticApiKeySecretDialog = ({
  secret,
  kind,
  onClose,
}: AtticApiKeySecretDialogProps) => {
  const intl = useIntl();
  const header = getAuthorizationHeader(secret ?? "");

  return (
    <DashboardModal open={secret !== null} onChange={() => undefined}>
      <DashboardModal.Content size="sm" data-test-id="attic-api-key-secret-dialog">
        <DashboardModal.Grid>
          <DashboardModal.Header>
            {intl.formatMessage(
              kind === "created"
                ? secretDialogMessages.createdTitle
                : secretDialogMessages.rotatedTitle,
            )}
          </DashboardModal.Header>
          <Text
            size={3}
            color="critical1"
            fontWeight="medium"
            data-test-id="attic-api-key-secret-warning"
          >
            {intl.formatMessage(secretDialogMessages.warning)}
          </Text>
          {kind === "rotated" && (
            <Text size={3}>{intl.formatMessage(secretDialogMessages.rotatedHint)}</Text>
          )}
          <Box {...panelStyles}>
            <Text size={3} fontWeight="medium">
              {intl.formatMessage(secretDialogMessages.secret)}
            </Text>
            <Box data-test-id="attic-api-key-secret-value">
              <Mono>{secret}</Mono>
            </Box>
            <CopyButton
              text={secret ?? ""}
              label={intl.formatMessage(secretDialogMessages.copySecret)}
              testId="attic-api-key-copy-secret"
            />
          </Box>
          <Box {...panelStyles}>
            <Text size={3} fontWeight="medium">
              {intl.formatMessage(secretDialogMessages.header)}
            </Text>
            <Box data-test-id="attic-api-key-secret-header">
              <Mono>{header}</Mono>
            </Box>
            <CopyButton
              text={header}
              label={intl.formatMessage(secretDialogMessages.copyHeader)}
              testId="attic-api-key-copy-header"
            />
          </Box>
          <DashboardModal.Actions>
            <Button variant="primary" onClick={onClose} data-test-id="attic-api-key-secret-done">
              {intl.formatMessage(secretDialogMessages.done)}
            </Button>
          </DashboardModal.Actions>
        </DashboardModal.Grid>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default AtticApiKeySecretDialog;
